import { OptionsApi } from '@core/swagger-client';
import { ErrorMessage, Input } from '@core/ui';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { usePasswordValidator, useValidations, useSubmitValidation } from '../../../hooks';
import { ChangePasswordFormCancelButton, ChangePasswordFormSaveButton, ChangePasswordFormTitle, PasswordEyeIcon } from '../../atoms';
import { ChangePasswordFormActions, ChangePasswordFormFields } from '../../molecules';

interface ChangePasswordState {
    currentPassword?: string | null;
    password?: string | null;
}

export const ChangePasswordForm = () => {
    const { t } = useTranslation();

    const {
        register,
        trigger,
        watch,
        getValues,
        reset,
        formState: { errors }
    } = useForm<ChangePasswordState>({ mode: 'onSubmit', reValidateMode: 'onSubmit', defaultValues: {} });
    const validate = useSubmitValidation({ watch, trigger });

    const { passwordValidation, validatePassword, passwordMessages } = usePasswordValidator();
    const { passwordRequired } = useValidations();

    const [currentPasswordType, setCurrentPasswordType] = useState('password');
    const [newPasswordType, setNewPasswordType] = useState('password');

    const [passwordScore, setPasswordScore] = useState<number>(-1);
    const password = watch('password');
    useEffect(() => {
        if (password) {
            validatePassword(password).then((result) => setPasswordScore(result.score));
        } else {
            setPasswordScore(-1);
        }
    }, [password]);

    const changePasswordMutation = useMutation(OptionsApi.login.changePassword());
    const changePassword = () => {
        validate(['currentPassword', 'password']).then((isValid) => {
            if (!isValid) return;

            const { currentPassword, password: newPassword } = getValues();
            changePasswordMutation.mutate({ currentPassword, newPassword }, { onSuccess: () => reset() });
        });
    };

    const togglePasswordVisibility = (passwordName: string) => {
        if (passwordName === 'currentPassword') {
            if (currentPasswordType === 'password') {
                setCurrentPasswordType('text');
            } else {
                setCurrentPasswordType('password');
            }
        } else if (passwordName === 'newPassword') {
            if (newPasswordType === 'password') {
                setNewPasswordType('text');
            } else {
                setNewPasswordType('password');
            }
        }
    };

    return (
        <>
            {changePasswordMutation.isSuccess && (
                <p data-testid="successMsg" className="pl-8 text-success pt-4">
                    {t('Account.Password.Changed.Message')}
                </p>
            )}

            <div className="p-4 md:p-8">
                <ChangePasswordFormTitle title={t('Account.Password.PageHeading')}></ChangePasswordFormTitle>

                {changePasswordMutation.error && <ErrorMessage apiError={changePasswordMutation.error} classNames="mb-4" />}
                <ChangePasswordFormFields className="max-w-lg">
                    <Input
                        type={currentPasswordType}
                        label={t('Account.Label.CurrentPassword')}
                        {...register('currentPassword', passwordRequired)}
                        errorMessage={errors.currentPassword?.message}
                        autoComplete="current-password"
                    >
                        <PasswordEyeIcon passwordType={currentPasswordType} onClick={() => togglePasswordVisibility('currentPassword')} />
                    </Input>
                    <Input
                        type={newPasswordType}
                        label={t('Account.Label.NewPassword')}
                        {...register('password', passwordValidation)}
                        {...passwordMessages(passwordScore, errors.password?.message)}
                        autoComplete="new-password"
                    >
                        <PasswordEyeIcon passwordType={newPasswordType} onClick={() => togglePasswordVisibility('newPassword')} />
                    </Input>
                </ChangePasswordFormFields>

                <ChangePasswordFormActions>
                    <ChangePasswordFormCancelButton onClick={() => reset()}> {t('Account.Button.Cancel')}</ChangePasswordFormCancelButton>
                    <ChangePasswordFormSaveButton data-testid="saveButton" disabled={changePasswordMutation.isPending} onClick={changePassword}>
                        {t('Account.Button.ChangePassword')}
                    </ChangePasswordFormSaveButton>
                </ChangePasswordFormActions>
            </div>
        </>
    );
};
